import { useState, useCallback } from "react";

import { useDropzone } from "react-dropzone";

import Alert from "ui-components/Alert";
import Button from "ui-components/Button";

import { UploadStatus } from "./types";

import FileStatus from "./File-Status";
import FileTag from "./File-Tag";

interface Props {
  handleSubmit?: (files: File[]) => any;
  onFileSelect?: (files: File[]) => any;
  maxFiles?: number;
  uploadStatus?: { [index: string]: UploadStatus };
}

const LocalFileUpload: React.FC<Props> = ({
  handleSubmit,
  onFileSelect,
  maxFiles = -1,
  uploadStatus = {},
}) => {
  const [files, setFiles] = useState<File[]>([]);

  const updateFiles = useCallback(
    (listing: File[]) => {
      setFiles(listing);
      if (onFileSelect) {
        onFileSelect(listing);
      }
    },
    [setFiles, onFileSelect]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      updateFiles([...files, ...acceptedFiles]);
    },
    [files, updateFiles]
  );

  const loading =
    Object.values(uploadStatus).filter((s) => !s.error && !s.completed)
      .length >= 1;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <div>
      {maxFiles >= 1 && files.length > maxFiles && (
        <Alert variant="error">
          You can only upload {maxFiles} more data. You are uploading{" "}
          {files.length - maxFiles} more.
        </Alert>
      )}
      {files
        .filter((f) => uploadStatus[f.name])
        .map((f) => (
          <FileStatus key={f.name} f={f} uploadStatus={uploadStatus[f.name]} />
        ))}
      <div
        className="box"
        style={{ height: "200px", border: "2px dashed grey" }}
        {...getRootProps()}
      >
        <input {...getInputProps()} data-testid={"inputSelector"} />
        <div className="flex flex-col justify-center h-1/1">
          {isDragActive ? (
            <p className="text-center">Drop the files here ...</p>
          ) : (
            <p className="text-center">
              Drag 'n' drop some files here, or click to select files
            </p>
          )}
        </div>
      </div>
      <div className="my-1">
        <div className="flex flex-wrap">
          {files.map((file) => (
            <FileTag
              key={file.name}
              file={file}
              onRemove={() =>
                updateFiles(files.filter((f) => f.name !== file.name))
              }
            />
          ))}
        </div>
      </div>
      {handleSubmit && (
        <div className="my-1">
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleSubmit(files)}
            disabled={loading}
          >
            {loading ? "Uploading..." : "Upload Selected Files"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default LocalFileUpload;
